import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { judgingAPI } from '../lib/api';
import { Badge, LoadingPage, PageHeader, Alert, Spinner, formatDate, SkillTags } from '../components/shared';
import { Star, ChevronLeft, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const CRITERIA = [
  { key: 'originality', label: 'Originality & Creativity', weight: 20, desc: 'How novel and creative is the approach?' },
  { key: 'technical_merit', label: 'Technical Merit', weight: 25, desc: 'Quality of technical implementation and rigor' },
  { key: 'feasibility', label: 'Feasibility', weight: 15, desc: 'Is the project achievable with available resources?' },
  { key: 'impact', label: 'Impact', weight: 25, desc: 'Potential benefit to society, industry, or academia' },
  { key: 'presentation', label: 'Presentation', weight: 15, desc: 'Clarity of communication and documentation quality' },
];

// ── Judge Project List ─────────────────────────────────────────────────────
export function JudgingListPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    judgingAPI.getAssigned()
      .then(r => setProjects(r.data))
      .catch(() => toast.error('Failed to load projects'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingPage />;

  const scored = projects.filter(p => p.score_id);
  const unscored = projects.filter(p => !p.score_id);

  return (
    <div>
      <PageHeader
        eyebrow="Judging"
        title="Assigned Projects"
        subtitle={`${scored.length} scored · ${unscored.length} remaining`}
      />

      {projects.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px 40px' }}>
          <Star size={40} style={{ color: 'var(--text-muted)', opacity: 0.3, margin: '0 auto 16px' }} />
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 400 }}>No projects assigned yet</h2>
          <p style={{ color: 'var(--text-muted)', marginTop: 8, fontSize: '0.875rem' }}>The admin will assign projects to you before judging begins.</p>
        </div>
      ) : (
        <>
          {unscored.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.08em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 16 }}>
                Awaiting Score ({unscored.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {unscored.map(p => <ProjectRow key={p.id} project={p} />)}
              </div>
            </div>
          )}

          {scored.length > 0 && (
            <div>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.08em', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 16 }}>
                Scored ({scored.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {scored.map(p => <ProjectRow key={p.id} project={p} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ProjectRow({ project }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '18px 20px',
      background: project.score_id ? 'rgba(34,197,94,0.04)' : 'var(--ink-mid)',
      border: `1px solid ${project.score_id ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.06)'}`,
      borderRadius: 'var(--radius-lg)',
    }}>
      <div>
        <div style={{ fontWeight: 500, marginBottom: 4 }}>{project.topic_title || project.title}</div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          {project.student_name} · {project.domain || 'General'}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {project.score_id ? (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--gold)', fontSize: '1.1rem', fontWeight: 600 }}>
              {parseFloat(project.my_score).toFixed(2)}/10
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--success)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <CheckCircle size={11} /> Scored
            </div>
          </div>
        ) : null}
        <Link to={`/dashboard/judging/${project.id}`} className={`btn btn-sm ${project.score_id ? 'btn-ghost' : 'btn-primary'}`}>
          {project.score_id ? 'Edit Score' : 'Score Now'}
        </Link>
      </div>
    </div>
  );
}

// ── Judge Scoring Page ─────────────────────────────────────────────────────
export function JudgeScoringPage() {
  const { id } = useParams();
  const [coiRequired, setCoiRequired] = useState(false);
  const [coiMeta, setCoiMeta] = useState({});
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scores, setScores] = useState({
    originality: 5, technical_merit: 5, feasibility: 5, impact: 5, presentation: 5, feedback: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    judgingAPI.getProject(id)
      .then(r => {
        setData(r.data);
        if (r.data.my_score) {
          const s = r.data.my_score;
          setScores({
            originality: s.originality, technical_merit: s.technical_merit,
            feasibility: s.feasibility, impact: s.impact, presentation: s.presentation,
            feedback: s.feedback || ''
          });
          setSubmitted(true);
        }
      })
      .catch(e => {
        if (e.response?.status === 423) {
          setCoiMeta({ projectId: id });
          setCoiRequired(true);
        } else {
          toast.error('Failed to load project');
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  const calculateTotal = () => {
    return (
      scores.originality * 0.20 +
      scores.technical_merit * 0.25 +
      scores.feasibility * 0.15 +
      scores.impact * 0.25 +
      scores.presentation * 0.15
    ).toFixed(2);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await judgingAPI.submitScore({ project_id: id, ...scores });
      toast.success(submitted ? 'Score updated!' : 'Score submitted!');
      setSubmitted(true);
      navigate('/dashboard/judging');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit score');
    } finally {
      setSubmitting(false);
    }
  };

  if (coiRequired) return (
    <COIModal
      projectId={id}
      projectTitle={coiMeta.projectTitle || 'Project'}
      studentName={coiMeta.studentName}
      lecturerName={coiMeta.lecturerName}
      onDeclared={(hasConflict) => {
        if (hasConflict) {
          setCoiRequired(false);
          toast.error('Conflict flagged. Admin has been notified for reassignment.');
          window.history.back();
        } else {
          setCoiRequired(false);
          setLoading(true);
          judgingAPI.getProject(id)
            .then(r => {
              setData(r.data);
              if (r.data.my_score) {
                const s = r.data.my_score;
                setScores({
                  originality: s.originality, technical_merit: s.technical_merit,
                  feasibility: s.feasibility, impact: s.impact, presentation: s.presentation,
                  feedback: s.feedback || ''
                });
                setSubmitted(true);
              }
            })
            .catch(() => toast.error('Failed to load project'))
            .finally(() => setLoading(false));
        }
      }}
    />
  );

  if (loading) return <LoadingPage />;
  if (!data) return null;

  const { project } = data;
  const total = calculateTotal();

  return (
    <div>
      <Link to="/dashboard/judging" className="btn btn-ghost btn-sm" style={{ marginBottom: 24, display: 'inline-flex', gap: 6 }}>
        <ChevronLeft size={16} /> Back to Projects
      </Link>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 24, alignItems: 'start' }}>
        {/* Project Details */}
        <div>
          <div style={{ marginBottom: 32 }}>
            {project.domain && <div className="eyebrow" style={{ marginBottom: 8 }}>{project.domain}</div>}
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 400, lineHeight: 1.2, marginBottom: 16 }}>
              {project.topic_title || project.title}
            </h1>
            <div style={{ display: 'flex', gap: 24, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              <span>Student: <strong style={{ color: 'var(--text-primary)' }}>{project.student_name}</strong></span>
              <span>Dept: <strong style={{ color: 'var(--text-primary)' }}>{project.department}</strong></span>
              {project.lecturer_name && <span>Lecturer: <strong style={{ color: 'var(--text-primary)' }}>{project.lecturer_name}</strong></span>}
            </div>
          </div>

          {project.entry_deck_path && (
            <div className="card" style={{ marginBottom: 20, border: '1px solid rgba(201,168,76,0.35)', background: 'rgba(201,168,76,0.04)' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 500, marginBottom: 12 }}>Entry Deck</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: 14 }}>
                This slide deck is the student's official finale entry — the primary material for your evaluation.
              </p>
              <a href={project.entry_deck_path} className="btn btn-primary" download>
                Download Entry Deck (.pptx)
              </a>
            </div>
          )}
          {project.gallery_image_paths?.length > 0 && (
            <div className="card" style={{ marginBottom: 20 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 500, marginBottom: 16 }}>Project Images</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
                {project.gallery_image_paths.map(path => (
                  <a key={path} href={path} target="_blank" rel="noopener noreferrer"
                     style={{ display: 'block', aspectRatio: '1', borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <img src={path} alt="Project" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </a>
                ))}
              </div>
            </div>
          )}
          {project.topic_description && (
          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 500, marginBottom: 16 }}>Topic Description</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7, fontSize: '0.9rem' }}>{project.topic_description}</p>
          </div>
          )}

          {project.required_skills?.length > 0 && (
            <div className="card" style={{ marginBottom: 20 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 500, marginBottom: 16 }}>Required Skills</h3>
              <SkillTags skills={project.required_skills} />
            </div>
          )}

          {project.milestones?.length > 0 && (
            <div className="card">
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 500, marginBottom: 16 }}>Project Milestones</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {project.milestones.map((m, i) => (
                  m && <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{m.title}</div>
                      {m.submitted_at && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Submitted {formatDate(m.submitted_at)}</div>}
                    </div>
                    <Badge status={m.status} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Scoring Panel */}
        <div className="card card-gold" style={{ position: 'sticky', top: 80 }}>
          <div style={{ marginBottom: 24 }}>
            <div className="eyebrow" style={{ marginBottom: 6 }}>Evaluation Form</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '3rem', fontWeight: 300, color: 'var(--gold)', lineHeight: 1 }}>
              {total}
              <span style={{ fontSize: '1.2rem', color: 'var(--text-muted)', marginLeft: 4 }}>/10</span>
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>Weighted composite score</div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginBottom: 24 }}>
            {CRITERIA.map(criterion => (
              <div key={criterion.key}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{criterion.label}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{criterion.desc}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: '1rem', color: 'var(--gold)', fontWeight: 600 }}>
                      {scores[criterion.key]}
                    </span>
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>({criterion.weight}%)</span>
                  </div>
                </div>
                <input
                  type="range"
                  min="0"
                  max="10"
                  step="0.5"
                  className="score-slider"
                  value={scores[criterion.key]}
                  onChange={e => setScores(s => ({ ...s, [criterion.key]: parseFloat(e.target.value) }))}
                  style={{ width: '100%' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 2 }}>
                  <span>Poor</span>
                  <span>Excellent</span>
                </div>
              </div>
            ))}
          </div>

          <div className="form-group" style={{ marginBottom: 20 }}>
            <label className="form-label">Feedback & Comments</label>
            <textarea
              className="form-textarea"
              style={{ minHeight: 90 }}
              placeholder="Provide constructive feedback for the student…"
              value={scores.feedback}
              onChange={e => setScores(s => ({ ...s, feedback: e.target.value }))}
            />
          </div>

          <button
            className="btn btn-primary"
            style={{ width: '100%' }}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? <Spinner size={16} /> : submitted ? 'Update Score' : 'Submit Evaluation'}
          </button>

          {submitted && (
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--success)', fontSize: '0.8rem', justifyContent: 'center' }}>
              <CheckCircle size={14} /> Score previously submitted
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ── COI Declaration Modal ──────────────────────────────────────────────────
function COIModal({ projectId, projectTitle, studentName, lecturerName, onDeclared }) {
  const [hasConflict, setHasConflict] = useState(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (hasConflict === null) { toast.error('Please make a selection'); return; }
    if (hasConflict && !note.trim()) { toast.error('Please describe the conflict'); return; }
    setSubmitting(true);
    try {
      await judgingAPI.declareCOI(projectId, { has_conflict: hasConflict, conflict_note: note });
      onDeclared(hasConflict);
    } catch {
      toast.error('Failed to submit declaration');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
    }}>
      <div style={{
        background: 'var(--ink-mid)', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 16, padding: 32, maxWidth: 520, width: '100%'
      }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: 'var(--gold)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 16 }}>
          Conflict of Interest Declaration
        </div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 500, marginBottom: 8, color: 'var(--text-primary)' }}>
          Before you access this project
        </h2>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 20 }}>
          Please confirm whether you have any personal or professional relationship with this project, its student, or supervisor. Your declaration is confidential.
        </p>

        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '14px 16px', marginBottom: 24, fontSize: '0.85rem' }}>
          <div style={{ fontWeight: 500, color: 'var(--text-primary)', marginBottom: 4 }}>{projectTitle}</div>
          <div style={{ color: 'var(--text-muted)' }}>
            {studentName && <span>Student: {studentName}</span>}
            {lecturerName && <span style={{ marginLeft: 16 }}>Supervisor: {lecturerName}</span>}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          <button
            onClick={() => setHasConflict(false)}
            style={{
              padding: '14px 16px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
              border: `1px solid ${hasConflict === false ? 'var(--teal)' : 'rgba(255,255,255,0.1)'}`,
              background: hasConflict === false ? 'rgba(42,157,143,0.1)' : 'transparent',
              color: 'var(--text-primary)', fontSize: '0.9rem',
            }}
          >
            <div style={{ fontWeight: 500, marginBottom: 2 }}>No conflict — I can judge this fairly</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>I have no personal or professional relationship with this project</div>
          </button>
          <button
            onClick={() => setHasConflict(true)}
            style={{
              padding: '14px 16px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
              border: `1px solid ${hasConflict === true ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)'}`,
              background: hasConflict === true ? 'rgba(239,68,68,0.08)' : 'transparent',
              color: 'var(--text-primary)', fontSize: '0.9rem',
            }}
          >
            <div style={{ fontWeight: 500, marginBottom: 2 }}>I have a conflict of interest</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>I know the student, supervisor, or have a stake in this project</div>
          </button>
        </div>

        {hasConflict === true && (
          <div className="form-group" style={{ marginBottom: 20 }}>
            <label className="form-label">Describe the conflict *</label>
            <textarea className="form-textarea" rows={3}
              placeholder="e.g. I am the student's supervisor / I co-authored related work…"
              value={note} onChange={e => setNote(e.target.value)} />
          </div>
        )}

        <button
          className={`btn ${hasConflict === true ? 'btn-danger' : 'btn-teal'}`}
          style={{ width: '100%', padding: 14 }}
          onClick={handleSubmit}
          disabled={submitting || hasConflict === null}
        >
          {submitting ? <Spinner size={16} /> : hasConflict === true ? 'Flag Conflict & Request Reassignment' : 'Confirm — No Conflict, Proceed'}
        </button>

        <div style={{ marginTop: 14, fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.6 }}>
          This declaration is immutable and logged. Contact admin@futurumawards.com if you have questions.
        </div>
      </div>
    </div>
  );
}
